import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload, 
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
  Clock, CheckCircle
} from 'lucide-react';
import { courseModules } from '../data/courseData';
import { useProgress } from '../context/ProgressContext';
import { usePurchase } from '../context/PurchaseContext';
import { Quiz } from '../components/course/Quiz';
import { Checklist } from '../components/course/Checklist';
import { FillInFields } from '../components/course/FillInFields';
import { PassphraseBuilder } from '../components/course/PassphraseBuilder';
import { TipBox } from '../components/course/TipBox';
import { ModuleNavigation } from '../components/course/ModuleNavigation';
import { LockedModuleOverlay } from '../components/course/LockedModuleOverlay';
import { cn } from '../lib/utils';

const iconMap = {
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload,
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
};

// Module 1 is free, rest require purchase
const FREE_MODULE_IDS = [1];

export default function ModulePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setCurrentModule, isModuleComplete } = useProgress();
  const { hasAccess, isLoading: isPurchaseLoading } = usePurchase();

  const module = courseModules.find(m => m.slug === slug);

  useEffect(() => {
    if (!module) {
      navigate('/');
      return;
    }
    setCurrentModule(module.id);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!module) {
    return null;
  }

  const Icon = iconMap[module.icon] || BookOpen;
  const isComplete = isModuleComplete(module.id);
  
  // Check if this module is locked
  const isFreeModule = FREE_MODULE_IDS.includes(module.id);
  const isLocked = !isFreeModule && !hasAccess && !isPurchaseLoading;

  return (
    <div className="max-w-3xl mx-auto fade-in">
      {/* Locked Module Overlay */}
      {isLocked && <LockedModuleOverlay />}

      {/* Module Header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 text-sm text-text-muted mb-4">
          <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-medium">
            Module {module.id} of {courseModules.length}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {module.estimatedTime}
          </span>
          {isComplete && (
            <span className="flex items-center gap-1 text-success">
              <CheckCircle className="w-4 h-4" />
              Completed
            </span>
          )}
          {isFreeModule && !hasAccess && (
            <span className="flex items-center gap-1 bg-secondary/20 text-secondary-foreground px-2 py-0.5 rounded-full text-xs font-medium">
              Free Preview
            </span>
          )}
        </div>

        <div className="flex items-start gap-4">
          <div className={cn(
            "w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0",
            isComplete ? "bg-success/10 text-success" : "bg-primary/10 text-primary"
          )}>
            <Icon className="w-8 h-8" />
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">
              {module.title}
            </h1>
            <p className="text-text-muted text-lg">
              {module.description}
            </p>
          </div>
        </div>
      </header>

      {/* Module Content */}
      <article className="space-y-8">
        {/* Introduction */}
        <section className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
          <p className="text-text text-lg leading-relaxed">
            {module.content.intro}
          </p>
        </section>

        {/* Content Sections */}
        {module.content.sections.map((section, index) => (
          <section 
            key={index} 
            className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 slide-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <h2 className="font-heading font-bold text-xl md:text-2xl text-text mb-4">
              {section.title}
            </h2>
            <ul className="space-y-3">
              {section.content.map((item, itemIndex) => (
                <li 
                  key={itemIndex} 
                  className="flex items-start gap-3 text-text-muted"
                >
                  <span className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {/* Warning (if exists) */}
        {module.content.warning && (
          <TipBox variant="warning" title="Important">
            {module.content.warning}
          </TipBox>
        )}

        {/* Tip Box (if exists) */}
        {module.content.tip && (
          <TipBox variant="tip" mascot={module.content.tip.mascot}>
            {module.content.tip.text}
          </TipBox>
        )}

        {/* Exercise - Passphrase Builder for module 2 */}
        {module.content.exercise?.type === 'passphrase-builder' && (
          <PassphraseBuilder />
        )}

        {/* Exercise - Website Checker */}
        {module.content.exercise?.type === 'website-checker' && (
          <div className="bg-gradient-to-br from-info/10 to-info/5 rounded-2xl border-2 border-info/20 p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center">
                <Globe className="w-6 h-6 text-info" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl text-text mb-1">
                  {module.content.exercise.title}
                </h3>
                <p className="text-text-muted text-sm">
                  {module.content.exercise.instructions}
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-info/20">
              <p className="text-text-muted text-sm mb-3">
                <strong>Try it now:</strong> Open a new tab and go to a website you use often 
                (like your bank or email). Look for:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span>A padlock icon in the address bar</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span>The address starts with <code className="bg-stone-100 px-2 py-0.5 rounded">https://</code></span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Checklist */}
        {module.checklist && (
          <Checklist moduleId={module.id} items={module.checklist} />
        )}

        {/* Fill-in Fields */}
        {module.fillInFields && (
          <FillInFields moduleId={module.id} fields={module.fillInFields} />
        )}

        {/* Quiz */}
        {module.quiz && (
          <Quiz moduleId={module.id} quiz={module.quiz} />
        )}

        {/* Module Navigation */}
        <ModuleNavigation currentModule={module} />
      </article>
    </div>
  );
}
